import { useEffect, useState } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import { DragDropContext, Droppable, Draggable, DraggableProvided } from 'react-beautiful-dnd';
import NoSsr from '@material-ui/core/NoSsr';
import logListofListsViewed from '@alltrails/analytics/events/logListofListsViewed';
import ListRowImage from '@alltrails/shared/components/ListRowImage';
import { reorder } from '@alltrails/shared/utils/lists/listHelpers';
import ListRow from 'components/ListRow';
import CustomProvider from 'components/CustomProvider';
import logError from 'utils/logError';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import List from 'types/List';
import ProfileUser from 'types/ProfileUser';
import { saveList } from '../../api/List';
import { wrapUrlSafe } from '../../utils/language_support_util';
import CreateListPopupButton from '../profile/CreateListPopupButton';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';

import * as styles from './styles/styles.module.scss';
import { XLATE_NONE } from '../../Constants';

type ViewableUser = {
  user: ProfileUser;
};

type Props = {
  lists: List[] | null;
  allowEditing: boolean;
  user: ViewableUser;
};

const ListContainer = ({ lists, allowEditing, user }: Props): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();
  const currentUser = useUser();

  const favoritesTitle =
    user?.user?.id === currentUser?.id ? (
      <FormattedMessage defaultMessage="My favorites" />
    ) : (
      <FormattedMessage defaultMessage="{name}'s favorites" values={{ name: user?.user?.first_name }} />
    );
  const favoriteList = { id: 1000, order: 999, title: favoritesTitle, name: favoritesTitle, slug: 'favorites', user };
  const [isListChanged, setIsListChanged] = useState(false);
  const [newList, setLists] = useState<List[] | null>(lists || null);

  const saveListOrder = () => {
    const ids = newList.map(item => item.id).reverse();

    saveList(ids)
      .then(() => setIsListChanged(false))
      .catch(e => logError(e));
  };

  useEffect(() => {
    if (isListChanged) {
      saveListOrder();
    }
  }, [isListChanged]);

  useEffect(() => {
    logListofListsViewed({ owned_by_user: allowEditing, owner_id: user?.user?.id as string, list_count: lists?.length as unknown as string });
  }, []);

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const reorderList = reorder(newList, result.source.index, result.destination.index);
    setIsListChanged(true);
    setLists(reorderList);
  };

  const favoritesUrl = wrapUrlSafe(`/members/${favoriteList.user?.user?.slug}/${favoriteList?.slug}`, languageRegionCode);

  const renderFavoritesRow = () => (
    <div className={styles.favoritesContainer} data-id="favorites" key="favorites">
      <ListRowImage listId={favoriteList.id} emptyList={false} />
      <div className={styles.titleAndContents}>
        <a href={favoritesUrl} className={`link ${XLATE_NONE}`}>
          <div className={styles.listTitle}>{favoriteList.name}</div>
        </a>
      </div>
    </div>
  );

  if (!lists) {
    return (
      <div className={styles.listContainer}>
        <CreateListPopupButton allowEditing={allowEditing} favoritesRow={renderFavoritesRow()} />
      </div>
    );
  }

  if (allowEditing && lists) {
    return (
      <NoSsr>
        <div className={styles.listContainer}>
          <CreateListPopupButton allowEditing={allowEditing} favoritesRow={renderFavoritesRow()} />
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided: any) => (
                <ol className={styles.lists} {...provided.droppableProps} ref={provided.innerRef}>
                  {newList?.map((list, idx) => (
                    <Draggable key={`${list.id}`} draggableId={`${list.id}`} index={idx}>
                      {(draggableProvided: DraggableProvided) => (
                        <ol ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                          <ListRow
                            lists={newList}
                            list={list}
                            idx={idx}
                            allowEditing={allowEditing}
                            setLists={setLists}
                            dragHandleProps={draggableProvided.dragHandleProps}
                          />
                        </ol>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ol>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </NoSsr>
    );
  }

  const items = newList?.map((item, idx) => <ListRow lists={newList} list={item} idx={idx} allowEditing={allowEditing} setLists={setLists} />);

  return (
    <CustomProvider>
      <div className={styles.listContainer}>
        {renderFavoritesRow()}
        <ol className={styles.lists}>{items}</ol>
      </div>
    </CustomProvider>
  );
};

export default function ListContainerWrapper({ lists, allowEditing, user }: Props) {
  return (
    <CustomProvider>
      <ListContainer lists={lists} allowEditing={allowEditing} user={user} />
    </CustomProvider>
  );
}
